import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, Snowflake, Wallet, X } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/sonner";
import { GymLoader } from "../../components/common/GymLoader";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/70" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-sm rounded-t-3xl md:rounded-2xl p-6 space-y-4" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}
      >
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold">{title}</h2>
          <button onClick={onClose}><X size={18} color="#A1A1AA" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

// UX-MEM-01 §1 SCR-P03-03 + highest-risk rule (self-serve freeze/transfer must respect real plan
// limits, never silently allow past them): `freeze_rules.max` vs `freezes_taken` is read from the
// real plan/membership documents and disables the button with an explicit reason once exhausted
// — the backend (`routers/memberships.py`'s unmodified `_optimistic_update`) enforces this too,
// this is just the honest client-side mirror of it.
export default function Membership() {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState(null);
  const [transferPhone, setTransferPhone] = useState("");
  const [transferReason, setTransferReason] = useState("");

  const membershipQ = useQuery({ queryKey: ["member-membership"], queryFn: async () => (await api.get("/member/membership")).data });
  const m = membershipQ.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["member-membership"] });
    queryClient.invalidateQueries({ queryKey: ["member-home"] });
  };

  const freezeMutation = useMutation({
    mutationFn: () => api.post("/member/membership/freeze", { expected_version: m.version }),
    onSuccess: () => { toast.success("Membership frozen."); setModal(null); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const transferMutation = useMutation({
    mutationFn: () => api.post("/member/membership/transfer", { expected_version: m.version, target_phone: transferPhone, reason: transferReason }),
    onSuccess: () => { toast.success("Membership transferred."); setModal(null); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });
  const renewMutation = useMutation({
    mutationFn: () => api.post("/member/membership/renew", { expected_version: m.version }),
    onSuccess: () => { toast.success("Renewed — new dues added, pay when ready."); invalidate(); },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (membershipQ.isLoading) return <GymLoader label="Loading membership…" testId="member-membership-loader" />;
  if (!m) return null;

  const freezesLeft = (m.freeze_rules?.max ?? 0) - (m.state?.freezes_taken ?? 0);
  const canFreeze = m.state?.freeze_state !== "frozen" && freezesLeft > 0;

  return (
    <div data-testid={MEMBER.membershipPage} className="space-y-5">
      <motion.div {...fadeUp(reducedMotion)}>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "#A1A1AA" }}>{m.plan_name}</p>
        <p className="font-heading text-4xl font-black mt-1">{m.state.remaining_days ?? "—"} <span className="text-lg font-medium" style={{ color: "#A1A1AA" }}>days left</span></p>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full inline-block mt-2" style={{ background: m.status === "active" ? "rgba(50,215,75,0.15)" : "rgba(214, 32, 43,0.15)", color: m.status === "active" ? "#32D74B" : "#D6202B" }}>{m.status}</span>
      </motion.div>

      {m.dues_balance > 0 && (
        <motion.div {...fadeUp(reducedMotion, 0.05)} className="rounded-2xl p-5" style={{ background: "#141414", border: "1px solid rgba(214, 32, 43,0.3)" }}>
          <p className="text-sm" style={{ color: "#A1A1AA" }}>Dues outstanding</p>
          <p className="font-heading text-2xl font-bold mt-1">₹{(m.dues_balance / 100).toLocaleString("en-IN")}</p>
          <Button data-testid={MEMBER.payDuesButton} onClick={() => navigate(`/member/pay?purpose=dues&amount=${m.dues_balance}`)} className="w-full mt-3 text-white active:scale-95 transition-transform" style={{ background: "#D6202B" }}>Pay now</Button>
        </motion.div>
      )}

      <motion.div {...fadeUp(reducedMotion, 0.08)} data-testid={MEMBER.walletBalance} className="rounded-2xl p-5 flex items-center justify-between" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <span className="flex items-center gap-2 text-sm" style={{ color: "#A1A1AA" }}><Wallet size={16} /> Wallet balance</span>
        <span className="font-heading text-xl font-bold">₹{(m.wallet_balance / 100).toLocaleString("en-IN")}</span>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.11)} className="grid grid-cols-2 gap-3">
        <Button data-testid={MEMBER.freezeButton} disabled={!canFreeze} variant="outline" onClick={() => setModal("freeze")} className="gap-1.5 border-white/15 text-white bg-transparent hover:bg-white/10 active:scale-95 transition-transform">
          <Snowflake size={14} /> Freeze {freezesLeft <= 0 && "(used up)"}
        </Button>
        <Button data-testid={MEMBER.transferButton} variant="outline" onClick={() => setModal("transfer")} className="border-white/15 text-white bg-transparent hover:bg-white/10 active:scale-95 transition-transform">Transfer</Button>
      </motion.div>
      <Button data-testid={MEMBER.renewButton} disabled={renewMutation.isPending} variant="outline" onClick={() => renewMutation.mutate()} className="w-full border-white/15 text-white bg-transparent hover:bg-white/10 active:scale-95 transition-transform">
        {renewMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : "Renew membership"}
      </Button>

      {modal === "freeze" && (
        <Modal title="Freeze membership?" onClose={() => setModal(null)}>
          <p className="text-sm" style={{ color: "#A1A1AA" }}>You have {freezesLeft} freeze(s) left on your plan. Your end date will pause until you unfreeze.</p>
          <Button data-testid={MEMBER.freezeConfirmButton} disabled={freezeMutation.isPending} onClick={() => freezeMutation.mutate()} className="w-full text-white" style={{ background: "#D6202B" }}>Confirm freeze</Button>
        </Modal>
      )}
      {modal === "transfer" && (
        <Modal title="Transfer membership" onClose={() => setModal(null)}>
          <Input data-testid={MEMBER.transferPhoneInput} placeholder="Friend's phone number" value={transferPhone} onChange={(e) => setTransferPhone(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" />
          <Input data-testid={MEMBER.transferReasonInput} placeholder="Reason" value={transferReason} onChange={(e) => setTransferReason(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white" />
          <Button data-testid={MEMBER.transferConfirmButton} disabled={!transferPhone.trim() || !transferReason.trim() || transferMutation.isPending} onClick={() => transferMutation.mutate()} className="w-full text-white" style={{ background: "#D6202B" }}>Confirm transfer</Button>
        </Modal>
      )}
    </div>
  );
}
